// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { useMemo, useState } from "react";
import { Empty, Input, Modal } from "antd";

// AddToCallModal picks people to add to a 1:1 call (escalating it to a group). Candidates
// are the people you already share a room with (minus whoever's already in the call); a
// search box also matches a raw user ID. Confirming invites + rings the selection.
export function AddToCallModal({
  open,
  candidates,
  onAdd,
  onClose,
}: {
  readonly open: boolean;
  readonly candidates: { userID: string; name: string }[];
  readonly onAdd: (userIDs: string[]) => void;
  readonly onClose: () => void;
}) {
  const [picked, setPicked] = useState<string[]>([]);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter((c) => c.name.toLowerCase().includes(q) || c.userID.toLowerCase().includes(q));
  }, [candidates, query]);

  // Allow adding a raw, fully-qualified user ID that isn't in the candidate list.
  const rawId = query.trim();
  const isRawUser = /^@[^:]+:.+/.test(rawId) && !candidates.some((c) => c.userID === rawId);

  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const confirm = () => {
    const ids = isRawUser && picked.length === 0 ? [rawId] : picked;
    if (ids.length === 0) return;
    onAdd(ids);
    setPicked([]);
    setQuery("");
    onClose();
  };

  return (
    <Modal
      open={open}
      title="Add people to the call"
      okText={picked.length > 1 ? `Add ${picked.length}` : "Add"}
      okButtonProps={{ disabled: picked.length === 0 && !isRawUser }}
      onOk={confirm}
      onCancel={() => {
        setPicked([]);
        setQuery("");
        onClose();
      }}
    >
      <Input.Search placeholder="Search people, or paste a @user:server id" value={query} onChange={(e) => setQuery(e.target.value)} style={{ marginBottom: 12 }} allowClear />
      <div className="addcall-list" style={{ maxHeight: 320, overflowY: "auto" }}>
        {isRawUser && (
          <button type="button" className="addcall-row" onClick={() => { onAdd([rawId]); setPicked([]); setQuery(""); onClose(); }}>
            <span className="addcall-avatar">@</span>
            <span className="addcall-name">Invite {rawId}</span>
          </button>
        )}
        {filtered.map((c) => (
          <button key={c.userID} type="button" className={`addcall-row${picked.includes(c.userID) ? " picked" : ""}`} onClick={() => toggle(c.userID)}>
            <span className="addcall-avatar">{c.name.replace(/^@/, "").charAt(0).toUpperCase() || "?"}</span>
            <span className="addcall-name">{c.name}</span>
            <span className="addcall-check">{picked.includes(c.userID) ? "✓" : ""}</span>
          </button>
        ))}
        {filtered.length === 0 && !isRawUser && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No one to add — paste a user id" />}
      </div>
    </Modal>
  );
}
